// Mi espacio › Hoy · panel de seguimiento · próxima acción asistida (pure, tested by scripts/test-next-actions.mjs).
// The code decides the situation, the action, the date, the reason and what is missing; the AI only writes the draft
// of a message; Ale confirms, edits or discards (agreed 04-11-2026). Nothing here writes or sends anything.
//
// Rules (suggestions Ale edits; working days are Monday to Friday in Hoy's time zone, no holidays assumed; every
// date counts from the fact it depends on, never from when the panel is opened, and a date already past becomes
// today: a task is never proposed with a past date):
//   R0 no interaction that counts yet → prepare the first contact (channel chosen by Ale, today by default)
//   R1 first contact without a registered answer → follow-up 3 working days after that contact
//   R2 second contact without a registered answer → follow-up 5 working days after the previous contact
//   R3 three or more contacts without a registered answer → resume in 30 calendar days (only a suggestion)
//   R4 the contact answered (the latest interaction is theirs) → answer today
//   R5 the contact asked for a proposal → prepare it in 2 working days, or by the date agreed explicitly
//   R6 a meeting is scheduled → prepare it the working day before (today if that moment already passed)
// "Sin respuesta registrada" never means that nobody answered: there may be other emails or channels Oike does not see.
import { outreachKind } from './outreachEvidence.js';
import { hasProposalRequest } from './proposalRequest.js';
import { localDay, addDaysIso, isValidTimeZone } from './coachingTime.js';

export const RULES_VERSION = 1;
export const RULE_LABELS = {
  R0: 'Preparar primer contacto',
  R1: 'Primer contacto sin respuesta registrada', R2: 'Segundo contacto sin respuesta registrada', R3: 'Tres contactos sin respuesta registrada',
  R4: 'Respondió', R5: 'Pidió una propuesta', R6: 'Reunión agendada',
};
/** No automatic contact follow-ups (Nurture keeps only the actions Ale plans herself). */
export const NO_PROPOSAL_STATUSES = ['Not Interested', 'DNC', 'Bounced', 'Left Company', 'Nurture'];
const CHANNEL_OF = { Email: 'correo', WhatsApp: 'WhatsApp', LinkedIn: 'LinkedIn', Phone: 'llamada', Meeting: 'en persona', Calendar: 'reunión' };

const field = (r, k) => { const v = r?.fields?.[k]; return v && typeof v === 'object' && !Array.isArray(v) ? (v.name ?? v.value ?? '') : (v ?? ''); };
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

// ── Working days ──
const weekday = (day) => new Date(`${day}T12:00:00Z`).getUTCDay();
export const isWorkingDay = (day) => ![0, 6].includes(weekday(day));
export function addWorkingDays(day, n) {
  let d = day; let left = n;
  while (left > 0) { d = addDaysIso(d, 1); if (isWorkingDay(d)) left--; }
  return d;
}
export function previousWorkingDay(day) {
  let d = addDaysIso(day, -1);
  while (!isWorkingDay(d)) d = addDaysIso(d, -1);
  return d;
}
/** The local day (YYYY-MM-DD) of a record's date in Hoy's zone; a plain date stays as it is. */
export function localDateOf(value, tz) {
  const v = String(value || '');
  if (ISO_DAY.test(v)) return v;
  const d = new Date(v);
  if (!v || Number.isNaN(d.getTime())) return null;
  return localDay(d, isValidTimeZone(tz) ? tz : 'UTC').day;
}

// ── Facts ──
const OP_RE = /\[op:([A-Za-z0-9-]{8,64})\]/;
/** The stable key of an interaction: the operation that wrote it (so the one being registered keeps its key once saved), else its id. */
export const basisKeyOf = (o) => (String(field(o, 'Notes')).match(OP_RE) || [])[1] || o?.id || '';

/** What one activity is for the rules, or null when it does not count (a channel opened, a draft…). */
export function factOf(o, tz) {
  const kind = outreachKind(o);
  if (kind === 'opened' || kind === 'ignored') return null;
  const status = String(field(o, 'Status') || '');
  const date = localDateOf(field(o, 'Date'), tz);
  if (!date) return null;
  const base = { id: o.id, key: basisKeyOf(o), date, at: String(field(o, 'Date') || ''), channel: String(field(o, 'Channel') || ''), status };
  if (status === 'Meeting Scheduled') return { ...base, type: 'meeting' };
  if (['Not Interested', 'Bounced'].includes(status)) return { ...base, type: 'closed' };
  if (kind === 'inbound') return { ...base, type: 'inbound', request: hasProposalRequest(field(o, 'Notes')) };
  return { ...base, type: 'contact', verified: kind === 'gmail_sent' };
}

/** The interaction being registered now in the panel (not saved yet), as a fact with the key it will keep. */
export function registrationFact({ outcome, channel = 'Email', op, today, meetingAt = '', tz, replyId = '' }) {
  if (!outcome || outcome === 'none') return null;
  if (outcome === 'proposal' && replyId) return { markReply: replyId }; // marks the selected answer: no new interaction
  const base = { id: '', key: op, date: today, at: '~', channel, status: '', pending: true }; // '~': after every record of the same day
  if (outcome === 'contacted') return { ...base, type: 'contact' };
  if (outcome === 'replied') return { ...base, type: 'inbound' };
  if (outcome === 'proposal') return { ...base, type: 'inbound', request: true };
  if (outcome === 'meeting') { const d = localDateOf(meetingAt, tz); return d ? { ...base, type: 'meeting', date: d, at: meetingAt, channel: 'Calendar' } : null; }
  return { ...base, type: 'closed' };
}

const byDate = (a, b) => String(a.date).localeCompare(String(b.date)) || String(a.at).localeCompare(String(b.at));

// ── Proposal ──
const fmtDay = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
export const dayLabel = fmtDay;
const factLabel = (f) => `${f.pending ? 'lo que registrás ahora' : fmtDay(f.date)}${f.channel ? ` · ${CHANNEL_OF[f.channel] || f.channel}` : ''}`;

/**
 * The proposal for one contact, or null. `records` are the contact's activities; `registration` what is being
 * registered now; `status` the contact's state after it; `contact` { name, email, phone, linkedin };
 * `agreedOn` an explicit agreed date for a proposal; `coverage` the last Gmail reading of this session (informative).
 */
/** The channel a first contact starts with (Ale can change it): email, else LinkedIn, else WhatsApp. */
export const firstChannelOf = (contact = {}) => (contact.email ? 'Email' : contact.linkedin ? 'LinkedIn' : contact.phone ? 'WhatsApp' : 'Email');
/** A first contact needs one line of context only when nothing says why to write: no role and no company. */
export const needsMotive = (contact = {}, company = '') => !String(contact.role || '').trim() && !String(company || '').trim();

export function proposalFor({ stakeholderId, records = [], registration = null, status = '', contact = {}, today, tz, agreedOn = '', coverage = null, firstChannel = '' }) {
  if (!stakeholderId || !ISO_DAY.test(today || '')) return null;
  if (NO_PROPOSAL_STATUSES.includes(String(status || '').trim())) return null;
  let facts = records.map(o => factOf(o, tz)).filter(Boolean);
  let markReply = '';
  if (registration?.markReply) {
    markReply = registration.markReply;
    facts = facts.map(f => (f.id === markReply ? { ...f, request: true } : f));
  } else if (registration) facts.push(registration);
  facts.sort(byDate);
  const last = facts[facts.length - 1];
  const name = contact.name || 'el contacto';
  if (!last) return firstContact({ stakeholderId, contact, name, today, channel: firstChannel || firstChannelOf(contact) });
  let rule; let ideal; let title; let reason; let basis = [last]; let draft = false; const missing = [];

  if (last.type === 'closed') return null;
  if (last.type === 'meeting') {
    if (last.date < today) return null; // the meeting already happened: what happened is registered first
    rule = 'R6'; ideal = previousWorkingDay(last.date); title = `Preparar la reunión con ${name}`;
    reason = `Hay una reunión agendada para el ${fmtDay(last.date)}: se prepara el día hábil anterior.`;
  } else if (last.type === 'inbound' && last.request) {
    rule = 'R5'; draft = true;
    ideal = ISO_DAY.test(agreedOn || '') ? agreedOn : addWorkingDays(last.date, 2);
    title = `Preparar y enviar la propuesta a ${name}`;
    reason = `Pidió una propuesta (${factLabel(last)}). ${ISO_DAY.test(agreedOn || '') ? 'Se usa la fecha acordada.' : 'Sugerencia: 2 días hábiles desde el pedido.'}`;
    if (!ISO_DAY.test(agreedOn || '')) missing.push('No hay una fecha acordada con el contacto: se sugieren 2 días hábiles.');
  } else if (last.type === 'inbound') {
    rule = 'R4'; draft = true; ideal = last.date; title = `Responder a ${name}`;
    reason = `Respondió (${factLabel(last)}) y es lo último registrado.`;
    missing.push('Oike no lee el texto de la respuesta: revisalo antes de usar el borrador.');
  } else {
    // Contacts in a row since the last answer
    const run = [];
    for (let i = facts.length - 1; i >= 0 && facts[i].type === 'contact'; i--) run.unshift(facts[i]);
    basis = run;
    const n = run.length;
    if (n === 1) { rule = 'R1'; draft = true; ideal = addWorkingDays(last.date, 3); title = `Seguimiento a ${name}`; reason = `Le escribiste (${factLabel(last)}) y no hay respuesta registrada. Sugerencia: 3 días hábiles.`; }
    else if (n === 2) { rule = 'R2'; draft = true; ideal = addWorkingDays(last.date, 5); title = `Segundo seguimiento a ${name}`; reason = `Dos contactos sin respuesta registrada; el último, ${factLabel(last)}. Sugerencia: 5 días hábiles desde ese contacto.`; }
    else { rule = 'R3'; ideal = addDaysIso(last.date, 30); title = `Retomar el contacto con ${name}`; reason = `${n} contactos sin respuesta registrada; el último, ${factLabel(last)}. Sugerencia: retomar en 30 días (no se crea sola ni cambia el estado).`; }
    missing.push('«Sin respuesta registrada» no significa que no haya respondido: puede haber correos archivados u otros canales que Oike no ve.');
    if (!coverage) missing.push('Gmail no se sincronizó en esta sesión: puede haber respuestas recientes sin registrar.');
    else if (!coverage.complete) missing.push(`La última lectura de Gmail de esta sesión quedó incompleta (${coverageText(coverage)}).`);
  }
  const dueOn = ideal < today ? today : ideal;
  const channel = rule === 'R6' ? '' : (last.channel && last.channel !== 'Calendar' ? last.channel : 'Email');
  const has = { Email: !!contact.email, WhatsApp: !!contact.phone, Phone: !!contact.phone, LinkedIn: !!contact.linkedin };
  if (draft && channel && channel in has && !has[channel]) missing.push(`No tiene ${channel === 'Email' ? 'email' : channel === 'LinkedIn' ? 'LinkedIn' : 'teléfono'} cargado para ${CHANNEL_OF[channel] || channel}.`);
  const alternatives = draft ? ['Email', 'WhatsApp', 'LinkedIn'].filter(c => c !== channel && has[c]) : [];
  const key = `${stakeholderId}:${rule}:v${RULES_VERSION}:${last.key}`;
  return {
    rule, version: RULES_VERSION, label: RULE_LABELS[rule], key, fp: fingerprint({ rule, title, ideal, channel }),
    title, dueOn, ideal, clamped: ideal < today, channel, alternatives, reason, missing, draft,
    basis: basis.map(f => ({ id: f.id || null, key: f.key, date: f.date, channel: f.channel, type: f.type, pending: !!f.pending })),
    markReply: markReply || null, pendingBasis: !!last.pending,
  };
}

/**
 * R0 · no interaction that counts: «Preparar primer contacto». One proposal per channel (its own key, so each channel
 * keeps its own draft and edits); the date is today and Ale changes it. Preparing it never registers anything.
 */
function firstContact({ stakeholderId, contact, name, today, channel }) {
  const has = { Email: !!contact.email, WhatsApp: !!contact.phone, LinkedIn: !!contact.linkedin };
  const missing = [];
  if (!has[channel]) missing.push(`No tiene ${channel === 'Email' ? 'email' : channel === 'LinkedIn' ? 'LinkedIn' : 'teléfono'} cargado para ${CHANNEL_OF[channel] || channel}.`);
  const title = `Contactar a ${name}`;
  return {
    rule: 'R0', version: RULES_VERSION, label: RULE_LABELS.R0, key: `${stakeholderId}:R0:v${RULES_VERSION}:first-contact-${channel.toLowerCase()}`,
    fp: fingerprint({ rule: 'R0', title, ideal: 'first', channel }), title, dueOn: today, ideal: today, clamped: false, channel,
    alternatives: ['Email', 'WhatsApp', 'LinkedIn'].filter(c => c !== channel && has[c]), channels: ['Email', 'WhatsApp', 'LinkedIn'],
    reason: 'No hay interacciones registradas con este contacto: Oike prepara el primer mensaje. Prepararlo no registra nada ni cambia su estado.',
    missing, draft: true, basis: [], markReply: null, pendingBasis: false,
  };
}

/** What a proposal says, without when it was computed: two proposals with the same fingerprint are the same suggestion. */
export const fingerprint = ({ rule, title, ideal, channel }) => `${rule}|${String(title).trim().toLowerCase()}|${ideal}|${channel || ''}`;

/**
 * Whether the proposal is shown: not when Ale already confirmed or discarded it (same key), nor when a newer
 * interaction leads to the same suggestion (same fingerprint as her last decision), nor when an open action
 * already carries it. Returns { show, reason }.
 */
export function visibility(proposal, { marks = [], openTasks = [] } = {}) {
  if (!proposal) return { show: false, reason: 'none' };
  if (marks.some(m => m.key === proposal.key)) return { show: false, reason: marks.find(m => m.key === proposal.key).state };
  const latest = [...marks].sort((a, b) => String(b.at).localeCompare(String(a.at)))[0];
  if (latest && latest.fp === proposal.fp) return { show: false, reason: latest.state };
  if (openTasks.some(t => t.source?.suggestion?.fp === proposal.fp || t.source?.suggestion?.key === proposal.key)) return { show: false, reason: 'planned' };
  return { show: true, reason: '' };
}

/** How a planned action compares with the proposal: 'same' (nothing to do) or 'different' (Ale decides). */
export function compareWithPlanned(proposal, task) {
  if (!proposal || !task) return null;
  const sameDay = task.dueOn === proposal.dueOn || task.dueOn === proposal.ideal;
  const sameTitle = String(task.title || '').trim().toLowerCase() === String(proposal.title).trim().toLowerCase();
  return sameDay || sameTitle ? 'same' : 'different';
}

/** What the screen compares before confirming: if it changed while Ale reviewed, she is told before anything is saved. */
export const contextOf = (proposal, openTasks = [], status = '') =>
  `${proposal?.key || ''}|${proposal?.fp || ''}|${status}|${openTasks.map(t => `${t.id}:${t.dueOn || ''}:${t.updatedAt || ''}`).sort().join(',')}`;

// ── Gmail coverage (informative only: never a proof for the server) ──
export function coverageText(c) {
  if (!c) return 'sin sincronizar en esta sesión';
  const at = (() => { try { return new Date(c.at).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return c.at; } })();
  return `${(c.folders || ['Recibidos', 'Enviados']).join(' y ')} desde el ${c.from}, leído el ${at}${c.complete ? ', lectura completa de esa consulta' : ', lectura parcial'}`;
}

// ── Draft for the AI (only the data Ale authorized for this function, 04-11-2026) ──
/**
 * Name, role, company, channel, language (country), her sender profile and the metadata of the relevant
 * interactions (date, channel, direction; never their text). Her note of the panel only when she marks it.
 * Never bodies, subjects, synced fragments, attachments or private notes. Returns the exact text sent.
 */
export function draftPrompt({ proposal, contact = {}, company = '', country = '', sender = {}, interactions = [], note = '', includeNote = false, motive = '' }) {
  const KIND = { R0: 'a short first message to a contact you have never written to (introduce yourself briefly, one concrete reason to talk, one easy question; no hard pitch)', R1: 'a short first follow-up after a message with no registered answer', R2: 'a second short follow-up after two messages with no registered answer', R4: 'a reply to the contact, who answered (you do not know what they wrote: do not invent it; keep it open and brief)', R5: 'a short acknowledgement that you will prepare and send the proposal (do not promise a date unless one is given below)' };
  const meta = interactions.slice(-6).map(f => `- ${f.date} · ${f.channel || 'sin canal'} · ${f.type === 'inbound' ? 'from the contact' : f.type === 'meeting' ? 'meeting' : 'from you'}`).join('\n');
  const lines = [
    `You are ${sender.senderName || 'the sender'}${sender.senderTitle ? `, ${sender.senderTitle}` : ''}${sender.companyName ? ` at ${sender.companyName}` : ''}.`,
    `Sign as "${sender.senderName || 'the sender'}". Never use placeholders.`,
    sender.voiceTone ? `Tone/voice: ${sender.voiceTone}` : '',
    '',
    `CONTACT: ${contact.firstName || contact.name || 'the contact'}${contact.role ? `, ${contact.role}` : ''}${company ? ` at ${company}` : ''}`,
    `CHANNEL: ${proposal.channel || 'Email'}`,
    `LANGUAGE: Use the language most appropriate for the contact${country ? ` (country: ${country})` : ''}.`,
    '',
    `TASK: write ${KIND[proposal.rule] || 'a short follow-up'}.`,
    proposal.rule === 'R5' && proposal.dueOn ? `Agreed or planned date for the proposal: ${proposal.dueOn}.` : '',
    'PREVIOUS INTERACTIONS (metadata only, oldest → newest):',
    meta || '- none registered',
    String(motive || '').trim() ? `WHY YOU ARE WRITING (from the sender): ${String(motive).trim().slice(0, 300)}` : '',
    includeNote && String(note || '').trim() ? `\nNOTE FROM THE SENDER (use it as context): ${String(note).trim().slice(0, 800)}` : '',
    '',
    proposal.channel === 'Email' ? 'Format: first line "Subject: …", blank line, then the body. Max 4 sentences.' : 'Max 3 sentences. No "Subject:" line.',
    'BANNED PHRASES: "hope this finds you", "following up", "checking in", "touching base", "just wanted to".',
    'This is a DRAFT the sender will review. Write ONE message only. No preamble or explanation.',
  ];
  return lines.filter((l, i) => l !== '' || lines[i - 1] !== '').join('\n').trim();
}

/** A short, stable hash of a text (to tell whether the data changed since a draft was prepared; never the text itself). */
export function hashText(t) {
  let h = 2166136261;
  for (const ch of String(t || '')) { h ^= ch.codePointAt(0); h = Math.imul(h, 16777619) >>> 0; }
  return h.toString(36);
}

/** The text a confirmed draft adds to the task notes: marked as a draft, never as sent. */
export const draftNoteText = (text, channel) => `Borrador de mensaje (no enviado${channel ? ` · ${CHANNEL_OF[channel] || channel}` : ''}):\n${String(text || '').trim()}`;
